/**
 * 「上次停在哪一页」的持久化（v0.65）。
 *
 * 用户 2026-10-17 原话：
 *   「在作业指导书页面刷新怎么跳回到主数据了」
 *
 * ## 为什么会跳回去（两处叠加，缺一不可）
 *
 * 1. `view` 是记在 localStorage 里的，但**还原时的白名单里没有 `sip`**：
 *    `v === 'workspace' || v === 'users' || ... ? v : 'parts'` ⇒ 读到 `sip`
 *    就落回 `parts`；
 * 2. 还原完紧接着那个 `setItem(view)` 又把 `parts` 写回去 —— 连「上次在
 *    作业指导书」这个线索本身也被抹掉，于是每次都从头开始。
 *
 * ★ 关键点：`sip` 是**需要目标**的一层（SIP 是某张图纸的产物）。只记 `view`
 *   等于没记 —— 没有目标那一层根本渲染不出来。所以 `view` 与 `sipEntry`
 *   必须**同一次读取、同一次还原**（见 `loadNav` / `saveNav`），
 *   这就是 `loadNav()` 存在的唯一理由：把「两个键」当成**一个快照**对待。
 *
 * ⚠️ 还原前**不做网络校验**（要校验就得等一个请求，会先闪一下零件列表）。
 *    图纸万一已被删掉，编辑页自己会报「打不开这张图纸的 SIP」并给返回键 ——
 *    比悄悄跳回列表更容易理解，也不会让人觉得"点了没反应"。
 *
 * ⚠️ 也不做「记住整条路径」的通用路由：项目刻意不引 react-router
 *    （见 App.tsx 的说明），这里只是把**已有的那点记忆**补全，不是造路由。
 */
import type { SipEntry } from '../types';

/** 顶层页面（与 App.tsx 的 `view` 一一对应，别在这里多一项）。 */
// v0.84：`tasks` = 检验任务列表（P3）。它和 `parts`/`sip` 同级 ——
//   任务既不属于某张图纸的编辑态，也不属于台账，是独立的一层。
export type NavView = 'parts' | 'workspace' | 'users' | 'sip' | 'trash'
  | 'settings' | 'tasks';

/** 🔴 白名单**唯一出处**。漏掉 `sip` 就是 v0.64 的那个 bug。 */
const VIEWS: readonly NavView[] = ['parts', 'workspace', 'users', 'sip', 'trash',
  'settings', 'tasks'];

const VIEW_KEY = 'gdt_balloon_view';
const SIP_KEY = 'gdt_balloon_sip_entry';
/** 每张图纸「看到哪一份」的键前缀（数值后缀 = drawing id）。 */
const POS_PREFIX = 'gdt_balloon_sip_pos_';

/** localStorage 在**无痕/被禁用**时会抛异常 —— 记忆功能失效可以接受，
 *  把整个页面打挂不行。所以统一从这一个入口拿。 */
function _ls(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function isNavView(v: unknown): v is NavView {
  return typeof v === 'string' && (VIEWS as readonly string[]).includes(v);
}

/** 这个入口能不能被还原。
 *
 *  `pick`（顶栏「作业指导书」→ 全库图纸选择列表）**没有目标也合法** ——
 *  它本来就渲染一个选择列表，不需要 id。
 */
export function isRestorableEntry(e: unknown): e is SipEntry {
  if (!e || typeof e !== 'object') return false;
  const o = e as { kind?: unknown; id?: unknown };
  if (o.kind === 'pick') return true;
  if (o.kind !== 'drawing' && o.kind !== 'part') return false;
  return typeof o.id === 'number' && Number.isInteger(o.id) && o.id > 0;
}

export function loadSipEntry(): SipEntry | null {
  const ls = _ls();
  if (!ls) return null;
  const raw = ls.getItem(SIP_KEY);
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;                  // 存坏了就当没有（不要把异常抛到渲染里）
  }
  if (!isRestorableEntry(parsed)) return null;
  if (parsed.kind === 'pick') return { kind: 'pick' };
  // 只回填认识的字段：localStorage 里可能是旧版本留下的多余键。
  const label = typeof parsed.label === 'string' ? parsed.label : undefined;
  return { kind: parsed.kind, id: parsed.id, label };
}

export function saveSipEntry(e: SipEntry | null): void {
  const ls = _ls();
  if (!ls) return;
  if (!e) {
    ls.removeItem(SIP_KEY);
    return;
  }
  const keep = e.kind === 'pick'
    ? { kind: 'pick' }
    : { kind: e.kind, id: e.id, label: e.label };
  ls.setItem(SIP_KEY, JSON.stringify(keep));
}

/**
 * 启动快照：`view` 与 `sipEntry` **必须出自同一次读取**。
 *
 * 返回的 `sipEntry` 只在 `view === 'sip'` 时非空 —— 在别的页面上留着一个
 * 目标只会让"下一次点作业指导书"打开的是上次那张（用户没这么要求过）。
 */
export function loadNav(): { view: NavView; sipEntry: SipEntry | null } {
  const ls = _ls();
  const raw = ls ? ls.getItem(VIEW_KEY) : null;
  const entry = loadSipEntry();
  let view: NavView = isNavView(raw) ? raw : 'parts';
  // `sip` 这一层没目标 ⇒ 渲染不出任何东西，退回零件列表
  // （这条与 App.tsx 里那个"没有入口就退回"的 effect 是同一口径的兜底）。
  if (view === 'sip' && !entry) view = 'parts';
  return { view, sipEntry: view === 'sip' ? entry : null };
}

/**
 * 记下「当前这一页」。
 *
 * 🔴 `sip` 必须**先写目标、再写页码**（顺序反了就有极小的窗口：刷新落在
 *    `sip` 而目标还没写进去 ⇒ `loadNav` 判没目标 ⇒ 退回零件列表，
 *    现象和这个 bug 一模一样）。
 */
export function saveNav(view: NavView, entry: SipEntry | null): void {
  const ls = _ls();
  if (!ls) return;
  if (view === 'sip') {
    if (!entry) {
      // 没有目标就不该记 `sip` —— 记了也还原不出来。
      ls.setItem(VIEW_KEY, 'parts');
      return;
    }
    saveSipEntry(entry);
    ls.setItem(VIEW_KEY, 'sip');
    return;
  }
  ls.setItem(VIEW_KEY, view);
}

/** 这张图纸上次看的是哪一份（`doc_kind` + `seq`）。 */
export function loadDocPos(drawingId: number): { kind: string; seq: number } | null {
  const ls = _ls();
  if (!ls) return null;
  const raw = ls.getItem(POS_PREFIX + drawingId);
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  const p = parsed as { kind?: unknown; seq?: unknown };
  if (!p || typeof p.kind !== 'string' || p.kind === '') return null;
  if (typeof p.seq !== 'number' || !Number.isInteger(p.seq) || p.seq < 0) return null;
  return { kind: p.kind, seq: p.seq };
}

/**
 * 记下这张图纸看到哪一份。
 *
 * ★ 按图纸分别记（不是全局一个值）：零件 A 停在制程、零件 B 停在成品，
 *   各自回去各自的位置。全局一个值的话，切一张图纸就把它覆盖掉了。
 */
export function saveDocPos(drawingId: number, kind: string, seq: number): void {
  const ls = _ls();
  if (!ls || !kind) return;
  ls.setItem(POS_PREFIX + drawingId, JSON.stringify({ kind, seq }));
}
